import React, { useState } from 'react';
import { ArrowUpDownIcon, SmartphoneIcon, CreditCardIcon, TabletIcon } from 'lucide-react';
import { TerminalData } from '../../utils/dashboardData';
interface TerminalPerformanceProps {
  data: TerminalData[];
}
type SortField = 'id' | 'type' | 'transactions' | 'salesValue' | 'successRate';
type SortOrder = 'asc' | 'desc';
const TerminalPerformance: React.FC<TerminalPerformanceProps> = ({
  data
}) => {
  const [sortField, setSortField] = useState<SortField>('salesValue');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  const getTerminalIcon = (type: string) => {
    switch (type) {
      case 'POS':
        return <CreditCardIcon className="h-4 w-4 text-blue-600" data-id="element-349" />;
      case 'SoftPOS':
        return <TabletIcon className="h-4 w-4 text-indigo-600" data-id="element-350" />;
      case 'MobilePOS':
        return <SmartphoneIcon className="h-4 w-4 text-violet-600" data-id="element-351" />;
      default:
        return <CreditCardIcon className="h-4 w-4 text-slate-600" data-id="element-352" />;
    }
  };
  const getSuccessRateBadge = (rate: number) => {
    if (rate >= 98) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (rate >= 95) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };
  return <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-0 h-full flex flex-col overflow-hidden" data-id="element-353">
      <div className="p-6 border-b border-slate-100" data-id="element-354">
        <h2 className="text-lg font-bold text-slate-900" data-id="element-355">
          Terminal Performance
        </h2>
        <p className="text-sm text-slate-500 font-medium mt-1" data-id="element-356">
          Device-level transaction metrics
        </p>
      </div>

      <div className="overflow-x-auto flex-1" data-id="element-357">
        <table className="w-full text-sm text-left" data-id="element-358">
          <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-id="element-359">
            <tr data-id="element-360">
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('id')} data-id="element-361">
                <div className="flex items-center" data-id="element-362">
                  Terminal ID{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-363" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('type')} data-id="element-364">
                <div className="flex items-center" data-id="element-365">
                  Type{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-366" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('transactions')} data-id="element-367">
                <div className="flex items-center justify-end" data-id="element-368">
                  Txns{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-369" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('salesValue')} data-id="element-370">
                <div className="flex items-center justify-end" data-id="element-371">
                  Value{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-372" />
                </div>
              </th>
              <th className="px-6 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('successRate')} data-id="element-373">
                <div className="flex items-center justify-end" data-id="element-374">
                  Success Rate{' '}
                  <ArrowUpDownIcon className="h-3.5 w-3.5 ml-1.5 text-slate-400" data-id="element-375" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-376">
            {sortedData.map(terminal => <tr key={terminal.id} className="even:bg-slate-50/50 hover:bg-slate-50 transition-colors" data-id="element-377">
                <td className="px-6 py-4 font-bold text-slate-900" data-id="element-378">
                  {terminal.id}
                </td>
                <td className="px-6 py-4" data-id="element-379">
                  <div className="flex items-center" data-id="element-380">
                    <div className="p-1.5 bg-white border border-slate-200 shadow-sm rounded-lg mr-2.5" data-id="element-381">
                      {getTerminalIcon(terminal.type)}
                    </div>
                    <span className="font-medium text-slate-700" data-id="element-382">
                      {terminal.type}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right font-semibold text-slate-700" data-id="element-383">
                  {terminal.transactions}
                </td>
                <td className="px-6 py-4 text-right font-bold text-slate-900" data-id="element-384">
                  ₨{terminal.salesValue.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right" data-id="element-385">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border ${getSuccessRateBadge(terminal.successRate)}`} data-id="element-386">
                    {terminal.successRate}%
                  </span>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};
export default TerminalPerformance;